import { CampaignAsset } from './types';
import { parseDate, toISODate, fmtShort } from './dates';
import { assetDisplayTitle } from './assetTitle';

/** Doreen's homepage capacity rules (2026-09-08):
 *  - Hero banners share 100% width per day: one 100%, or two 50% — hard.
 *  - Promotional Banner: one per day — hard.
 *  - Product Carousel / Product Grid / Navigation Bar: two on one day is
 *    possible but needs attention — soft. */
export type CapacitySeverity = 'hard' | 'soft';

const heroWidth = (t: string): number =>
  t === 'Hero Banner 100%' ? 100 : t === 'Hero Banner 50%' ? 50 : 0;

const SINGLE_HARD = new Set(['Homepage Promotional Banner']);
const SINGLE_SOFT = new Set(['Homepage Product Carousel', 'Homepage Product Grid', 'Homepage Collection Cards', 'Navigation Bar']);

export interface DayCapacityIssue {
  severity: CapacitySeverity;
  message: string;
}

/** Issues for one calendar day, given every homepage asset live that day. */
export const dayCapacityIssues = (dayAssets: CampaignAsset[]): DayCapacityIssue[] => {
  const issues: DayCapacityIssue[] = [];
  const width = dayAssets.reduce((sum, a) => sum + heroWidth(a.asset_type), 0);
  if (width > 100) {
    issues.push({ severity: 'hard', message: `Hero banners at ${width}% (max 100%)` });
  }
  const counts = new Map<string, number>();
  for (const a of dayAssets) counts.set(a.asset_type, (counts.get(a.asset_type) ?? 0) + 1);
  for (const [type, n] of counts) {
    if (n < 2) continue;
    if (SINGLE_HARD.has(type)) {
      issues.push({ severity: 'hard', message: `${n}× ${type.replace('Homepage ', '')} on one day (max 1)` });
    } else if (SINGLE_SOFT.has(type)) {
      issues.push({ severity: 'soft', message: `${n}× ${type.replace('Homepage ', '')} on one day — possible, but not ideal` });
    }
  }
  return issues;
};

export interface PlanningConflict {
  severity: CapacitySeverity;
  /** The existing asset the candidate collides with. */
  asset: CampaignAsset;
}

const rangesOverlap = (aStart: Date, aEnd: Date, bStart: Date, bEnd: Date) =>
  aStart <= bEnd && bStart <= aEnd;

/** Conflicts a candidate placement (type + date range) would create against
 *  the existing homepage schedule. `excludeId` skips the asset being edited. */
export const planningConflicts = (
  candidateType: string,
  publishDate: string,
  endDate: string | null,
  existing: CampaignAsset[],
  excludeId?: number,
): PlanningConflict[] => {
  const start = parseDate(publishDate);
  const end = endDate ? parseDate(endDate) : start;
  const candWidth = heroWidth(candidateType);
  const out: PlanningConflict[] = [];

  const overlapping = existing.filter((a) => {
    if (a.id === excludeId || !a.publish_date) return false;
    const s = parseDate(a.publish_date);
    const e = a.end_date ? parseDate(a.end_date) : s;
    return rangesOverlap(start, end, s, e);
  });

  if (candWidth > 0) {
    // Per-day hero width including the candidate. A 50% next to one 50% is
    // fine; a 100% next to anything (or a third 50%) is not.
    const byDay = new Map<string, CampaignAsset[]>();
    for (const a of overlapping) {
      if (heroWidth(a.asset_type) === 0) continue;
      const s = parseDate(a.publish_date as string);
      const e = a.end_date ? parseDate(a.end_date) : s;
      const c = new Date(Math.max(s.getTime(), start.getTime()));
      const stop = new Date(Math.min(e.getTime(), end.getTime()));
      while (c <= stop) {
        const iso = toISODate(c);
        byDay.set(iso, [...(byDay.get(iso) ?? []), a]);
        c.setDate(c.getDate() + 1);
      }
    }
    const flagged = new Set<number>();
    for (const dayAssets of byDay.values()) {
      const width = candWidth + dayAssets.reduce((sum, a) => sum + heroWidth(a.asset_type), 0);
      if (width > 100) {
        for (const a of dayAssets) {
          if (!flagged.has(a.id)) {
            flagged.add(a.id);
            out.push({ severity: 'hard', asset: a });
          }
        }
      }
    }
  } else if (SINGLE_HARD.has(candidateType) || SINGLE_SOFT.has(candidateType)) {
    const severity: CapacitySeverity = SINGLE_HARD.has(candidateType) ? 'hard' : 'soft';
    for (const a of overlapping) {
      if (a.asset_type === candidateType) out.push({ severity, asset: a });
    }
  }
  return out;
};

/** One human line per conflicting asset, for the modal warning. */
export const conflictLine = (c: PlanningConflict): string => {
  const a = c.asset;
  const range = a.end_date && a.end_date !== a.publish_date
    ? `${fmtShort(a.publish_date as string)} → ${fmtShort(a.end_date)}`
    : fmtShort(a.publish_date as string);
  return `${assetDisplayTitle(a, { withCampaign: true })} (${range})`;
};
